import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  NotificationActionButtonType,
  NotificationStatus,
  QueryKeys,
} from "@/constants";
import ScreenRouteNames, {
  getRouteNameForCode,
} from "@/constants/screen-route-names";
import {
  useNotificationStatus,
  useRecommendationStatus,
} from "@/hooks/use-notification";
import {
  queryClient,
  updateNotificationStatusCache,
} from "@/hooks/use-query-client";
import useUserInfo from "@/hooks/use-user-info";
import { formatDateHumanReadable } from "@/lib/date-utils";

import { htmlFromString, lowerCaseComparison } from "@/lib/string-util";
import type { Notification, Recommendation } from "@/model/notification";
import type ActionButton from "@/model/action-button";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { capitalize } from "lodash";
import { Bookmark, ThumbsUp } from "lucide-react";
import { AuthMode } from "@/context/auth-provider";
import {
  getDirection,
  makeCall,
  openEmailApp,
  openExternalLink,
} from "@/lib/notification-utils";
import { useUserLocation } from "@/context/location-provider";
import { useDispatch } from "react-redux";
import { setIsOpen } from "@/store/chatbot/chatbot-slice";

export const NotificationActionButton = ({
  notification,
  limit,
  onClose,
}: {
  notification: Notification;
  limit?: number | null;
  onClose?: () => void;
}) => {
  const userInfo = useUserInfo();
  const dispatch = useDispatch();
  const { mutate: updateNotificationStatus } = useNotificationStatus();
  const actions = notification?.actionButton?.slice(
    0,
    limit ?? notification?.actionButton?.length,
  );
  // console.log("### debug", actions);
  const navigate = useNavigate();
  const { location: userLocation, isFetched: isUserLocationFetched } =
    useUserLocation();

  const markAsRead = () => {
    if (
      notification?.isRead ||
      lowerCaseComparison(userInfo.mode, AuthMode.GUEST)
    )
      return;
    updateNotificationStatusCache(
      userInfo.username,
      notification,
      NotificationStatus.OPEN,
    );
    updateNotificationStatus({
      userInfo,
      notification,
      status: NotificationStatus.OPEN,
    });
  };

  const onSelect = (action: ActionButton) => {
    markAsRead();
    switch (action?.type) {
      case NotificationActionButtonType.APP_ROUTE: {
        const path = getRouteNameForCode(action?.parameter ?? "");
        if (path) {
          if (path === ScreenRouteNames.SURVEY.path) {
            const outgoingTrackId =
              Boolean(notification?.outgoingTrackId) &&
              notification?.outgoingTrackId !== "N/A"
                ? notification?.outgoingTrackId
                : null;
            navigate(`/${ScreenRouteNames.SURVEY.path}`, {
              state: {
                surveyTitle: notification?.subject
                  ? htmlFromString(notification?.subject)
                  : "Survey",
                templateId: action?.templateId,
                outgoingTrackId,
              },
              replace: false,
            });
          } else if (path === ScreenRouteNames.CHAT.path) {
            if (onClose) onClose();
            dispatch(setIsOpen(true));
          } else {
            navigate(`/${path}`, { replace: false });
          }
        }
        break;
      }

      case NotificationActionButtonType.PHONE: {
        const phoneNum = action?.parameter ?? "";
        makeCall(phoneNum);
        break;
      }

      case NotificationActionButtonType.WEB_LINK: {
        const url = /^(https?:\/\/)/.test(action?.parameter)
          ? action?.parameter
          : `https://${action?.parameter}`;
        openExternalLink(url, userInfo);
        break;
      }

      case NotificationActionButtonType.MAP: {
        const { lat, lng } = action?.parameter
          ? JSON.parse(action?.parameter)
          : { lat: null, lng: null };
        if (lat && lng && isUserLocationFetched && userLocation) {
          const providerLat = parseFloat(lat);
          const providerLng = parseFloat(lng);
          getDirection(providerLat, providerLng, userLocation);
        }
        break;
      }
      case NotificationActionButtonType.EMAIL:
        openEmailApp(action?.parameter);
        break;

      default:
        break;
    }
  };

  const isGuest = lowerCaseComparison(userInfo.mode, AuthMode.GUEST);

  return (
    <div className="mt-2 flex flex-wrap justify-center gap-3">
      {actions?.map((action: ActionButton, index: number) => {
        const label = capitalize(action?.name ?? "");
        return (
          <Tooltip key={`${action?.name ?? `name${index}`}`}>
            <TooltipTrigger asChild>
              <span>
                <Button
                  className="rounded hover:cursor-pointer hover:bg-primary-extraLight"
                  variant="outline"
                  disabled={isGuest}
                  onClick={() => onSelect(action)}
                >
                  {label}
                </Button>
              </span>
            </TooltipTrigger>
            {isGuest && (
              <TooltipContent className="z-[200]">
                <p>{label} disabled for guest mode</p>
              </TooltipContent>
            )}
          </Tooltip>
        );
      })}
    </div>
  );
};

export const RenderSocialButton = ({
  messageId,
  queryKey = QueryKeys.NOTIFICATIONS,
}: {
  messageId: string;
  queryKey?: QueryKeys.NOTIFICATIONS | QueryKeys.RECOMMENDATIONS;
}) => {
  const userInfo = useUserInfo();
  const { mutate: updateNotificationStatus } = useNotificationStatus();
  const { mutate: updateRecommendationStatus } = useRecommendationStatus();
  const isRecommendation = queryKey === QueryKeys.RECOMMENDATIONS;

  const cacheData: (Notification | Recommendation)[] | undefined =
    queryClient.getQueryData([queryKey, userInfo.username]);

  const currentItem = cacheData?.find(
    (item) => item.messageId === messageId,
  ) as Notification | Recommendation;

  const handleSocialButtonClick = (
    type: NotificationStatus.LIKE | NotificationStatus.SAVE,
  ) => {
    switch (type) {
      case NotificationStatus.LIKE: {
        const status = currentItem?.isLiked
          ? NotificationStatus.UNLIKE
          : NotificationStatus.LIKE;
        //update cache
        updateNotificationStatusCache(
          userInfo.username,
          currentItem,
          status,
          queryKey,
        );
        //update api
        if (isRecommendation) {
          updateRecommendationStatus({
            userInfo,
            recommendation: currentItem as Recommendation,
            status,
          });
        } else {
          updateNotificationStatus({
            userInfo,
            notification: currentItem as Notification,
            status,
          });
        }

        break;
      }

      case NotificationStatus.SAVE: {
        const status = currentItem?.saveFlag
          ? NotificationStatus.UNSAVE
          : NotificationStatus.SAVE;
        //update cache
        updateNotificationStatusCache(
          userInfo.username,
          currentItem,
          status,
          queryKey,
        );
        //update api
        if (isRecommendation) {
          updateRecommendationStatus({
            userInfo,
            recommendation: currentItem as Recommendation,
            status,
          });
        } else {
          updateNotificationStatus({
            userInfo,
            notification: currentItem as Notification,
            status,
          });
        }

        break;
      }

      default:
        break;
    }
  };

  const isGuest = lowerCaseComparison(userInfo.mode, AuthMode.GUEST);

  return (
    <div className="mt-4 flex items-center justify-center gap-4">
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <Button
              className={`flex items-center gap-x-1 rounded px-3 py-2 text-sm shadow-sm hover:bg-gray-100 ${
                currentItem?.isLiked ? "text-blue-500" : "text-gray-400"
              }`}
              variant="ghost"
              onClick={() => handleSocialButtonClick(NotificationStatus.LIKE)}
              disabled={isGuest}
            >
              <ThumbsUp
                size={16}
                className={`${currentItem?.isLiked ? "fill-blue-500" : "fill-gray-400"} stroke-none`}
              />
              <span>Like</span>
            </Button>
          </span>
        </TooltipTrigger>
        {isGuest && (
          <TooltipContent className="z-[200]">
            <p>Like disabled for guest mode</p>
          </TooltipContent>
        )}
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <Button
              className={`flex items-center gap-x-1 rounded px-3 py-2 text-sm shadow-sm hover:bg-gray-100 ${
                currentItem?.saveFlag ? "text-blue-500" : "text-gray-400"
              }`}
              variant="ghost"
              onClick={() => handleSocialButtonClick(NotificationStatus.SAVE)}
              disabled={isGuest}
            >
              <Bookmark
                size={16}
                className={`${currentItem?.saveFlag ? "fill-blue-500" : "fill-gray-400"} stroke-none`}
              />
              <span>Save</span>
            </Button>
          </span>
        </TooltipTrigger>
        {isGuest && (
          <TooltipContent className="z-[200]">
            <p>Save disabled for guest mode</p>
          </TooltipContent>
        )}
      </Tooltip>
    </div>
  );
};

const NotificationDetail = ({
  notification,
  onClose,
  queryKey = QueryKeys.NOTIFICATIONS,
}: {
  notification: Notification;
  onClose?: () => void;
  queryKey?: QueryKeys.NOTIFICATIONS | QueryKeys.RECOMMENDATIONS;
}) => {
  const { mutate: updateNotificationStatus } = useNotificationStatus();

  const userInfo = useUserInfo();

  useEffect(() => {
    //ignore the status update when isRead is true, and only notifications track OPEN status
    if (
      queryKey !== QueryKeys.NOTIFICATIONS ||
      Boolean(notification?.isRead) ||
      lowerCaseComparison(userInfo.mode, AuthMode.GUEST)
    )
      return;
    //update cache
    updateNotificationStatusCache(
      userInfo.username,
      notification,
      NotificationStatus.OPEN,
      queryKey,
    );
    //update api
    updateNotificationStatus(
      {
        userInfo,
        notification,
        status: NotificationStatus.OPEN,
      },
      {
        onSuccess: () => {
          // console.log("### onSuccess");
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-autos flex max-w-2xl flex-col space-y-4 rounded-2xl p-5">
      <p className="text-xs text-gray-500">{`Date: ${formatDateHumanReadable(notification?.createdTime ?? "")}`}</p>

      <Label className="text-base font-normal">
        {notification?.body ? htmlFromString(notification?.body) : ""}
      </Label>
      {/*Notification Action buttons*/}
      <NotificationActionButton
        notification={notification}
        onClose={onClose}
        // onSelect={() => {}}
      />
      {/*Notification Social Button*/}
      {notification?.messageId !== "N/A" ? (
        <RenderSocialButton
          messageId={notification?.messageId}
          queryKey={queryKey}
        />
      ) : null}
    </div>
  );
};

export default NotificationDetail;
